import { useEffect, useRef } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import type { Event } from '../../models/Event';
import { FaTimes } from 'react-icons/fa';
import { format } from 'date-fns';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RHFTextArea } from '../../components/RHF/RHFTextArea';
import { RHFSubmitButton } from '../../components/RHF/RHFSubmitButton';
import { handleError } from '../../apis';
import EventApi from '../../apis/event_api';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { eventKeys } from '../../queries/EventQueries';

const validator_alpacaEvent = z.object({
  alpacaId: z.number(),
  alpacaEventId: z.number(),
  eventId: z.number(),
  applicationUserId: z.string(),
  note: z.string().trim().nullable(),
});

export default function EditAlpacaEvent() {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { alpacaEventId } = useParams();
  const event = useOutletContext<Event>();
  const queryClient = useQueryClient();

  useEffect(() => {
    const ref = dialogRef.current;
    ref?.showModal();

    return () => {
      ref?.close();
    };
  }, []);

  const alpacaEvent = event?.alpacasForEvent?.find(
    (x) => x.alpacaEventId == +alpacaEventId!,
  );

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: zodResolver(validator_alpacaEvent),
    defaultValues: alpacaEvent,
  });

  const onHandleSubmit = async (
    values: z.infer<typeof validator_alpacaEvent>,
  ) => {
    try {
      await EventApi.updateAlpacaEvent(values.alpacaEventId, values);
      queryClient.invalidateQueries({
        queryKey: eventKeys.byId(values.eventId),
      });
      navigate(`/events/${values.eventId}`);
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className='bg-white text-gray-600 m-auto
    dark:bg-gray-800  dark:tracking-wider dark:text-gray-400 '
    >
      <div className='flex justify-end'>
        <button
          className='my-2 rounded-md border border-slate-950 bg-transparent
     p-2 text-xs font-bold uppercase text-gray-600 hover:border-gray-400
      hover:text-gray-400 dark:border-gray-500 dark:text-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-400'
          onClick={() => navigate(-1)}
        >
          <FaTimes />
        </button>
      </div>
      <div className='flex flex-col items-center justify-center'>
        <h3 className='text-center text-2xl dark:text-gray-300'>
          {event && format(new Date(event?.date ?? ''), 'MM/dd/yyyy')}{' '}
          {event?.eventTypeId == 7
            ? event.otherEventName
            : event?.eventType?.eventTypeName}{' '}
          Event
        </h3>
        <h4>{alpacaEvent?.alpaca?.name}</h4>
        <form
          onSubmit={handleSubmit(onHandleSubmit)}
          className='flex flex-col items-center justify-center w-full min-w-[300px] max-w-[800px] '
        >
          <div className='w-full min-w-[300px]'>
            <div className='sm:col-span-2'>
              <RHFTextArea
                name='note'
                label='Note'
                className='min-w-[275px] '
                register={register('note')}
                errors={errors}
                rows={5}
              />
            </div>

            <div className='grid grid-cols-2 gap-4 mt-4 justify-items-center'>
              <button
                type='button'
                onClick={() => navigate(-1)}
                className='my-2 w-full max-w-68.75 rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
              >
                Cancel
              </button>
              <RHFSubmitButton label='Submit' isSubmitting={isSubmitting} />
            </div>
          </div>
        </form>
      </div>
    </dialog>
  );
}
